import React, { useEffect, useState } from "react";
import bgImage from "../assets/images/37.png";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  checkTeamMemberStatus,
  changeTeamMemberStatus,
} from "../features/team-member/teamMemberSlice";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import LoadingComponent from "../components/common/LoadingComponent";
import { saveAuthToken, getRedirectPath } from "../utils/authHeader";
import { setUser } from "../features/user/userSlice";
import { toast } from "react-toastify";

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Name is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

export const initialValue = {
  name: "",
  password: "",
};

function TeamInvitation() {
  const [rejected, setRejected] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loguuid, action } = useParams();
  const { invitationStatus, loading } = useSelector(
    (state) => state.teamMember,
  );

  useEffect(() => {
    if (loguuid) {
      dispatch(checkTeamMemberStatus(loguuid));
    }
  }, [dispatch, loguuid]);

  useEffect(() => {
    const rejectInvitation = async () => {
      if (
        action === "reject" &&
        loguuid &&
        invitationStatus &&
        invitationStatus.status === "INVITED"
      ) {
        const result = await dispatch(
          changeTeamMemberStatus({
            uuid: loguuid,
            status: "REJECTED",
          }),
        ).unwrap();

        if (result.success) {
          setRejected(true);
        }
      }
    };

    rejectInvitation();
  }, [dispatch, action, loguuid, invitationStatus]);

  const handleSubmit = async (values) => {
    const { data } = await dispatch(
      changeTeamMemberStatus({
        uuid: loguuid,
        status: "ACCEPTED",
        name: values.name,
        password: values.password,
      }),
    ).unwrap();

    const { token, role = "", ...user } = data;

    if (token) {
      saveAuthToken(token);
    }

    dispatch(setUser({ ...user, role }));

    toast.success("Login successful");

    navigate(
      getRedirectPath({
        role,
        isOnboardingCompleted: data.isOnboardingCompleted,
      }),
      { replace: true },
    );
  };

  return (
    <main className="main" id="top">
      <div className="container-fluid bg-body-tertiary dark__bg-gray-1200">
        <div
          className="bg-holder bg-auth-card-overlay"
          style={{ backgroundImage: `url(${bgImage})` }}
        ></div>

        <div className="row flex-center position-relative min-vh-100 g-0 py-5">
          <div className="col-11 col-sm-10 col-xl-8">
            <div className="card border border-translucent auth-card">
              <div className="card-body pe-md-0">
                {loading ? (
                  <LoadingComponent loading={true} />
                ) : (
                  <div className="row align-items-center gx-0 gy-7">
                    <div className="col mx-auto py-8">
                      <div className="auth-form-box text-center">
                        <a
                          className="d-flex flex-center text-decoration-none mb-4"
                          href="/"
                        >
                          <div className="d-flex align-items-center fw-bolder fs-3 d-inline-block">
                            <img
                              src={
                                localStorage.getItem("phoenixTheme") === "dark"
                                  ? "/assets/img/icons/logo.png"
                                  : "/assets/img/icons/logo-dark.png"
                              }
                              alt="logo"
                              width="150"
                            />
                          </div>
                        </a>

                        {rejected ? (
                          <>
                            <h3 className="text-body-highlight mb-3">
                              Invitation Rejected Successfully
                            </h3>
                            <p className="text-body-tertiary mb-0">
                              You have successfully declined this invitation.
                            </p>
                          </>
                        ) : invitationStatus?.success ? (
                          <>
                            <h3 className="text-body-highlight mb-1">
                              Join Your Team
                            </h3>
                            <p className="text-body-tertiary mb-2 mt-2">
                              You've been invited to join your team's workspace.
                              Enter your name and create a password to complete
                              your account setup.
                            </p>

                            <div className="d-flex align-items-center mt-5 mb-4">
                              <div className="flex-grow-1 border-top"></div>
                            </div>

                            <Formik
                              initialValues={initialValue}
                              validationSchema={validationSchema}
                              onSubmit={handleSubmit}
                            >
                              {({ isSubmitting }) => (
                                <Form noValidate>
                                  <div className="mb-3 text-end">
                                    <label
                                      className="form-label"
                                      htmlFor="name"
                                    >
                                      Full Name
                                    </label>
                                    <Field
                                      id="name"
                                      name="name"
                                      type="text"
                                      placeholder="Name"
                                      className="form-control"
                                    />
                                    <ErrorMessage
                                      name="name"
                                      component="div"
                                      className="invalid-feedback d-block"
                                    />
                                  </div>

                                  <div className="mb-3 text-end">
                                    <label
                                      className="form-label"
                                      htmlFor="password"
                                    >
                                      Password
                                    </label>
                                    <Field
                                      id="password"
                                      name="password"
                                      type="password"
                                      placeholder="Password"
                                      className="form-control"
                                      autoComplete="new-password"
                                    />
                                    <ErrorMessage
                                      name="password"
                                      component="div"
                                      className="invalid-feedback d-block"
                                    />
                                  </div>

                                  <button
                                    className="btn btn-primary d-block w-100 mt-3"
                                    type="submit"
                                    disabled={isSubmitting}
                                  >
                                    {isSubmitting
                                      ? "Please wait..."
                                      : "Join Team"}
                                  </button>
                                </Form>
                              )}
                            </Formik>
                          </>
                        ) : (
                          <>
                            <h3 className="text-body-highlight mb-3">
                              Invitation Not Available
                            </h3>
                            <p className="text-body-tertiary mb-0">
                              {invitationStatus?.message ||
                                "This invitation is invalid, has expired, or has already been accepted."}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default TeamInvitation;
